<script lang="ts">
  import Textfield from '@smui/textfield';
  import CharacterCounter from '@smui/textfield/character-counter';
  import Dialog, { Content as DialogContent, Actions, Title } from '@smui/dialog';
  import type { Plan } from '../../../firebase/common/plan';
  import { uploadPublicImage } from '../../../firebase/api/upload';
  import { deletePlan, upsertPlan } from '../../../firebase/api/plan';
  import CircularProgress from '@smui/circular-progress/src/CircularProgress.svelte';
  import Button from '../../../widgets/Button.svelte';
  import ImagePicker from '../../../widgets/ImagePicker.svelte';

  export let editPlan: boolean = false;
  export let newPlan: boolean = false;
  export let editingPlan: Omit<Plan, 'stripe_price_id' | 'stripe_product_id'> = {
    name: '',
    description: '',
    thumbnail: '',
    price: 500,
    created_at: 0,
  };
  export let channel_id: string;

  export let plans: Plan[] = [];

  let previewURL: string = '';
  $: previewURL = editingPlan.thumbnail;

  let thumbnail: File | null = null;
  let blocker: boolean = false;

  const checkValidity = (ev: Event) => {
    console.log('checkValidity');
    const target = ev.target as HTMLInputElement;
    const value = parseInt(target.value);

    if (plans.find((p) => p.price === value) === undefined) {
      target.setCustomValidity('');
      return;
    }

    target.setCustomValidity('この金額のプランは既に存在します');
    return;
  };

  const onSubmit = (ev: SubmitEvent) => {
    ev.preventDefault();

    if (
      newPlan &&
      !confirm('この内容でプランを作成しますか？\n注意: 支援者ついた場合、プランは削除できなくなります。')
    ) {
      return;
    }

    const upsert = async () => {
      if (newPlan && thumbnail === null) {
        alert('サムネイル画像を選択してください');
        return;
      }

      blocker = true;

      if (thumbnail !== null) {
        const url = await uploadPublicImage(thumbnail, false);

        if (!url) {
          alert('サムネイル画像のアップロードに失敗しました');
          return;
        }

        editingPlan.thumbnail = url;
      }

      await upsertPlan(channel_id, Math.floor(editingPlan.price).toString(), editingPlan);
      editPlan = false;
      blocker = false;

      location.reload();
    };

    upsert();
  };

  const toDeletePlan = async () => {
    if (
      !confirm(
        '本当にこのプランを削除しますか？ この操作は取り消せません。\n注意: このプランが最後のプランの場合、全ての投稿が全体公開になります。',
      )
    ) {
      return;
    }

    blocker = true;

    const status = await deletePlan(channel_id, Math.floor(editingPlan.price).toString());

    if (status == 204) {
      location.reload();
      return;
    }

    if (status == 412) {
      alert('このプランにはすでに購読者がいるため、削除できません。');
      blocker = false;
      return;
    }

    alert('プランの削除に失敗しました。');
    blocker = false;
  };

  $: {
    if (!editPlan) {
      thumbnail = null;
    }
  }
</script>

<Dialog
  bind:open={editPlan}
  aria-labelledby="large-scroll-title"
  aria-describedby="large-scroll-content"
  surface$style="width: 850px; max-width: calc(100vw - 32px);"
>
  <Title>プラン{newPlan ? '作成' : '設定'}</Title>
  <form action="." on:submit={onSubmit}>
    <DialogContent>
      <Textfield bind:value={editingPlan.name} label="プラン名" style="width: 100%" input$maxlength={32} required>
        <CharacterCounter slot="helper">0 / 32</CharacterCounter>
      </Textfield>
      <Textfield
        bind:value={editingPlan.description}
        label="プラン説明"
        style="width: 100%"
        input$maxlength={200}
        textarea
        required
      >
        <CharacterCounter slot="helper">0 / 200</CharacterCounter>
      </Textfield>
      <div class="thumbnail-settings">
        <ImagePicker
          crop={{ width: 960, height: 640 }}
          bind:image={thumbnail}
          maxSizeMB={0.75}
          defaultImage={editingPlan.thumbnail ?? null}
          onImageSelected={(file) => {
            if (file === undefined) {
              return;
            }

            console.log(file.size);
          }}
          title="サムネイル画像 (640×960)"
        />
      </div>
      {#if newPlan}
        <div on:input={checkValidity}>
          <Textfield
            bind:value={editingPlan.price}
            label="価格"
            style="width: 100%"
            input$max={10000}
            input$min={100}
            type="number"
            suffix="円/月"
            required
          />
        </div>
      {/if}
    </DialogContent>
    <Actions style="justify-content: center; column-gap: 2rem;">
      <Button type="submit" style="margin: 0.5rem 0;">
        {newPlan ? 'プランを作成' : '設定を完了'}
      </Button>
      <Button
        type="button"
        onclick={() => {
          editPlan = false;
        }}
        style="margin: 0.5rem 0;"
      >
        キャンセル
      </Button>
      <Button type="button" onclick={toDeletePlan} style="margin: 0.5rem 0;">プランを削除</Button>
    </Actions>
  </form>
  <div class="blocker" class:shown={blocker}>
    <div class="spinner-wrapper">
      <CircularProgress indeterminate style="width: 4rem; height: 4rem;" />
    </div>
  </div>
</Dialog>

<style lang="scss">
  .blocker {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-flow: column;
    justify-content: center;
    background: #f0f8ffa0;
    opacity: 0;
    pointer-events: none;

    &.shown {
      opacity: 1;
      pointer-events: all;
    }

    .spinner-wrapper {
      display: flex;
      justify-content: center;
    }
  }

  .thumbnail-settings {
    width: 24rem;
    max-width: calc(100vw - 80px);
  }
</style>
